import React, { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import { useAsrComparison } from "../../hooks/useAsrComparison";

const ALLOWED_EMAIL = "arsalanali@hlgsolutions.com";

const cardStyle = {
  backgroundColor: "var(--bg-elevated)",
  borderRadius: "8px",
  padding: "24px",
  color: "var(--text-primary)",
};

const inputStyle = {
  backgroundColor: "var(--bg-elevated-2)",
  color: "var(--text-primary)",
};

function EngineResult({ title, badgeColor, engine, costLabel }) {
  if (!engine) return null;

  return (
    <div className="col-md-6">
      <div style={cardStyle} className="h-100">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            <span className={`badge bg-${badgeColor} me-2`}>{title}</span>
          </h5>
          <small className="text-secondary">
            {engine.transcriptionMs ? `${(engine.transcriptionMs / 1000).toFixed(1)}s` : "-"} ·{" "}
            {costLabel}: ${Number(engine.estCost || 0).toFixed(5)}
          </small>
        </div>

        {engine.note && (
          <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: "0.85rem" }}>
            {engine.note}
          </div>
        )}

        {engine.error ? (
          <div className="alert alert-danger py-2 px-3 mb-0" style={{ fontSize: "0.85rem" }}>
            Transcription failed: {engine.error}
          </div>
        ) : (
          <>
            <div className="mb-3">
              <div className="text-secondary small mb-1">Raw transcript</div>
              <div
                className="p-2 rounded"
                style={{ backgroundColor: "var(--bg-elevated-2)", maxHeight: "160px", overflowY: "auto", whiteSpace: "pre-wrap", fontSize: "0.85rem" }}
              >
                {engine.transcript || <span className="text-muted">(empty)</span>}
              </div>
            </div>

            <div className="mb-3">
              <div className="text-secondary small mb-1">Labeled transcript</div>
              <div
                className="p-2 rounded"
                style={{ backgroundColor: "var(--bg-elevated-2)", maxHeight: "160px", overflowY: "auto", whiteSpace: "pre-wrap", fontSize: "0.85rem" }}
              >
                {engine.labeledTranscript || <span className="text-muted">(empty)</span>}
              </div>
            </div>

            {engine.qc && (
              <div>
                <div className="text-secondary small mb-1">QC result</div>
                <table className="table table-sm table-borderless mb-0" style={{ fontSize: "0.85rem" }}>
                  <tbody>
                    <tr>
                      <td className="text-secondary" style={{ width: "140px" }}>Disposition</td>
                      <td>{engine.qc.disposition}</td>
                    </tr>
                    <tr>
                      <td className="text-secondary">Sub-disposition</td>
                      <td>{engine.qc.sub_disposition || "-"}</td>
                    </tr>
                    <tr>
                      <td className="text-secondary">Reason</td>
                      <td>{engine.qc.reason || "-"}</td>
                    </tr>
                    <tr>
                      <td className="text-secondary">Summary</td>
                      <td>{engine.qc.summary || "-"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const AsrComparison = () => {
  const { user } = useAuth();
  const [audioFile, setAudioFile] = useState(null);
  const [campaignName, setCampaignName] = useState("");
  const fileInputRef = useRef(null);
  const { state, compare } = useAsrComparison();

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!audioFile) {
        toast.error("Choose an audio file first");
        return;
      }
      try {
        await compare({ audioFile, campaignName: campaignName.trim() });
      } catch (err) {
        toast.error(err.message || "Comparison failed");
      }
    },
    [audioFile, campaignName, compare]
  );

  // The real access control is server-side (routes/asrComparisonRoutes.js
  // checks req.user.email from the verified JWT) - this is just so the page
  // doesn't render its contents for anyone else who navigates here directly.
  if (String(user?.email || "").toLowerCase().trim() !== ALLOWED_EMAIL) {
    return (
      <div className="p-4">
        <div className="alert alert-secondary">This page isn't available for your account.</div>
      </div>
    );
  }

  const { result, isLoading } = state;

  return (
    <div className="p-4">
      <h2 className="text-white mb-1">ASR Comparison</h2>
      <p className="text-secondary mb-4">
        Upload a recording to compare Deepgram against Qwen3-ASR-1.7B side by side - transcript, speaker
        labeling, QC disposition, cost, and processing time for each.
      </p>

      <div className="mb-4" style={{ ...cardStyle, maxWidth: "560px" }}>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Audio file</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.ogg,.webm,.aac,.flac"
              className="form-control"
              style={inputStyle}
              onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
              disabled={isLoading}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Campaign name (optional)</label>
            <input
              type="text"
              className="form-control"
              style={inputStyle}
              placeholder="e.g. Medicare-Spanish-IB-RTB"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              disabled={isLoading}
            />
            <div className="form-text">Affects which campaign-specific QC rules apply (e.g. Medicare gating).</div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={isLoading || !audioFile}>
            {isLoading ? "Comparing... this can take 30-60s" : "Compare"}
          </button>
        </form>
      </div>

      {result && (
        <>
          <div className="text-secondary mb-3">Audio duration: {Number(result.audioDurationSec || 0).toFixed(1)}s</div>
          <div className="row g-3">
            <EngineResult title="Deepgram" badgeColor="info" engine={result.deepgram} costLabel="est. cost" />
            <EngineResult title="Qwen3-ASR-1.7B" badgeColor="success" engine={result.qwen} costLabel="est. cost" />
          </div>
        </>
      )}
    </div>
  );
};

export default AsrComparison;
